import moment from 'moment';

import { convertFromPermillionNumber } from '../../components/Competitions/Helpers';
import { PerformanceActivitiesDetail } from '../../reducers/ProfileReducer/PerformanceReducer/PerformanceReducer.types';
import Highcharts from 'highcharts';

const universeColors = ['#2991D1', '#DC636D', '#3B9D41', '#00B295'];
const benchmarkProperties = new Set(['benchmark-pnl', 'benchmark-sharpe', 'benchmark-turnover']);
const equalWeightProperties = new Set(['equal-weight-pnl', 'equal-weight-sharpe', 'equal-weight-turnover']);
const riskNeutralizedProperties = new Set(['risk-neutralized-pnl']);
const investabilityProperties = new Set(['investability-constrained-pnl']);
const regionProperties = new Set([
  'amer-pnl',
  'apac-pnl',
  'emea-pnl',
  'amer-sharpe',
  'apac-sharpe',
  'emea-sharpe',
  'amer-turnover',
  'apac-turnover',
  'emea-turnover',
]);
const regionColors: Record<string, string> = {
  'amer-pnl': '#7972d8',
  'apac-pnl': '#d68d83',
  'emea-pnl': '#ca76c3',
  'amer-sharpe': '#7972d8',
  'apac-sharpe': '#d68d83',
  'emea-sharpe': '#ca76c3',
  'amer-turnover': '#7972d8',
  'apac-turnover': '#d68d83',
  'emea-turnover': '#ca76c3',
};
const nonZonePnlProperties = new Set([
  ...benchmarkProperties,
  ...equalWeightProperties,
  ...riskNeutralizedProperties,
  ...investabilityProperties,
  ...regionProperties,
]);

interface ChartProperty {
  name: string;
  title: string;
  type: string;
}

function getColor(isSuperAlpha: boolean, recordSet: string, property: ChartProperty, index: number) {
  if (recordSet === 'data-explorer-coverage' || recordSet === 'data-explorer-value-range' || recordSet === 'distribution') {
    // @ts-ignore
    return universeColors[index];
  }
  if (property?.name && benchmarkProperties.has(property?.name)) {
    return '#f69526';
  }

  if (property?.name && riskNeutralizedProperties.has(property?.name)) {
    return '#60ca68';
  }

  if (property?.name && investabilityProperties.has(property?.name)) {
    return '#ACD147';
  }

  if (property?.name && regionProperties.has(property?.name)) {
    return regionColors[property.name] || '#59cdd5';
  }

  return isSuperAlpha &&
    ((recordSet === 'pnl' && property.name !== 'pnl') ||
      (recordSet === 'sharpe' && property.name !== 'sharpe') ||
      (recordSet === 'turnover' && property.name !== 'turnover'))
    ? '#58677a'
    : '#59cdd5';
}

function getFillColor(recordSet: string, index: number) {
  if (recordSet === 'data-explorer-coverage' || recordSet === 'data-explorer-value-range' || recordSet === 'distribution') {
    // @ts-ignore
    return {
      fillColor: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, new Highcharts.Color(universeColors[index]).setOpacity(0.2).get('rgba')],
          [0.1, new Highcharts.Color(universeColors[index]).setOpacity(0.05).get('rgba')],
          [0.3, new Highcharts.Color(universeColors[index]).setOpacity(0).get('rgba')],
        ],
      },
    };
  }
  return {};
}

function getIsNoZoneChart(recordSet: string, isSuperAlpha: boolean, property: ChartProperty) {
  return recordSet === 'coverage' || nonZonePnlProperties.has(property.name);
}

interface PerformanceRecordSeriesOptions {
  chartDataSchema: Record<string, any>;
  chartDataRecords: (number | string)[][];
  recordSet: string;
  osStartDate?: string;
  testStartDate?: string;
  validationStartDate?: string;
  trainStartDate?: string;
  isSuperAlpha?: boolean;
  isTestPeriodShown?: boolean;
}

interface ZoneColorsConfig {
  osStartDate?: string;
  trainStartDate?: string;
  testStartDate?: string;
  validationStartDate?: string;
}

interface ColorZone {
  color: string;
  value?: number;
}

function getZoneColorsConfig({ osStartDate, testStartDate, trainStartDate, validationStartDate }: ZoneColorsConfig) {
  //if there is a validation or test period, then graph should begin with train period:
  const trainGraphStartDate = trainStartDate ?? (testStartDate || validationStartDate) ? '1' : undefined;
  const timePeriods = [
    { name: 'is', startDate: '1', color: '#59cdd5' },
    { name: 'train', startDate: trainGraphStartDate, color: '#59cdd5' },
    { name: 'validation', startDate: validationStartDate, color: '#d55997' },
    { name: 'test', startDate: testStartDate, color: '#e57500' },
    { name: 'os', startDate: osStartDate, color: '#47b74a' },
  ];

  //backend provides start dates, but highcharts use end dates for each period (value)
  const periodsWithStartDates = timePeriods.filter(period => period.startDate);
  const resultingColors: ColorZone[] = periodsWithStartDates.map((period, i) => ({
    value: periodsWithStartDates[i + 1] ? moment(periodsWithStartDates[i + 1].startDate).valueOf() : undefined,
    color: period.color,
    className: period.name,
  }));

  return resultingColors;
}

interface FormatChartDataParams {
  chartDataRecords: (number | string)[][];
  indexOfDate: any;
  property: any;
  index: number;
  indexOfProperty: number;
  isTestPeriodHidden: boolean;
  testStartDate?: string;
}

interface SelectedPeriodRecordsParameters {
  isTestPeriodHidden: boolean;
  testStartDate: string | undefined;
  chartDataRecords: (number | string)[][];
  indexOfDate: number;
}

function getSelectedPeriodRecords({ isTestPeriodHidden, testStartDate, chartDataRecords, indexOfDate }: SelectedPeriodRecordsParameters) {
  if (isTestPeriodHidden && testStartDate) {
    const startDateIndex = getStartDateIndex(chartDataRecords, indexOfDate, testStartDate);
    return chartDataRecords.slice(0, startDateIndex);
  } else {
    return chartDataRecords;
  }
}

export function formatChartData({
  chartDataRecords,
  indexOfDate,
  property,
  index,
  indexOfProperty,
  isTestPeriodHidden,
  testStartDate,
}: FormatChartDataParams) {
  const selectedPeriodRecords = getSelectedPeriodRecords({
    isTestPeriodHidden: isTestPeriodHidden,
    testStartDate: testStartDate,
    chartDataRecords: chartDataRecords,
    indexOfDate: indexOfDate,
  });
  return selectedPeriodRecords.map((val: any) => [
    moment.utc(val[indexOfDate]).valueOf(),
    property.type === 'percent' ? val[index + indexOfProperty] * 100 : val[index + indexOfProperty],
  ]);
}

export const updatedRecordSeriesPerformance = ({
  chartDataSchema,
  chartDataRecords,
  recordSet,
  osStartDate,
  testStartDate,
  validationStartDate,
  isSuperAlpha = false,
  isTestPeriodShown = false,
}: PerformanceRecordSeriesOptions) => {
  const indexOfProperty: number = chartDataSchema.properties.findIndex((property: any) => property.name !== 'date');
  const indexOfDate = chartDataSchema.properties.findIndex((property: any) => property.name === 'date');
  let zonesConfig: any = [];
  if (osStartDate || testStartDate || validationStartDate) {
    zonesConfig = getZoneColorsConfig({ osStartDate, testStartDate, validationStartDate });
  } else {
    if (recordSet !== 'data-explorer-coverage' && recordSet !== 'data-explorer-value-range') {
      zonesConfig = [{ color: '#59cdd5' }];
    }
  }

  return chartDataSchema.properties
    .filter((property: any) => property.name !== 'date')
    .map((property: any, index: number) => ({
      color: getColor(isSuperAlpha, recordSet, property, index),
      ...getFillColor(recordSet, index),
      showInLegend: chartDataSchema.properties.length > 2,
      name: property.title,
      data: formatChartData({
        chartDataRecords: chartDataRecords,
        indexOfDate: indexOfDate,
        property: property,
        index: index,
        indexOfProperty: indexOfProperty,
        isTestPeriodHidden: !isTestPeriodShown,
        testStartDate,
      }),
      lineWidth: 1,
      states: {
        hover: {
          enabled: true,
          lineWidth: 1,
        },
      },
      zones: getIsNoZoneChart(recordSet, isSuperAlpha, property) ? null : zonesConfig,
      zoneAxis: 'x',
      dataGrouping: {
        units: [['day', [1]]],
        approximation: (groupedData: any[]) => groupedData[0],
      },
    }));
};

const updatedRecordSeriesDistribution = ({ chartDataSchema, chartDataRecords, recordSet, isSuperAlpha = false }: any) => {
  const indexOfProperty: number = chartDataSchema.properties.findIndex((property: any) => property.name !== 'date');
  const indexOfDate = chartDataSchema.properties.findIndex((property: any) => property.name === 'date');

  return chartDataSchema.properties
    .filter((property: any) => property.name !== 'date')
    .map((property: any, index: number) => ({
      color: getColor(isSuperAlpha, recordSet, property, index),
      gapSize: 5,
      showInLegend: chartDataSchema.properties.length > 2,
      name: property.title,
      data: chartDataRecords.map((val: any) => [
        val[indexOfDate],
        property.type === 'percent' ? val[index + indexOfProperty] * 100 : val[index + indexOfProperty],
      ]),
      lineWidth: 1,
      states: {
        hover: {
          enabled: true,
          lineWidth: 1,
        },
      },
      yAxis: index,
      zones: null,
      zoneAxis: 'x',
      type: property.name === 'CDF' ? 'spline' : 'column',
    }));
};

const updatedRecordSeriesHighStockChart = ({
  chartDataSchema,
  chartDataRecords,
  name,
  osStartDate,
  testStartDate,
  validationStartDate,
  isSuperAlpha = false,
  isTestPeriodShown = false,
}: any) => {
  const indexOfProperty: number = chartDataSchema.properties.findIndex((property: any) => property.name !== 'date');
  const indexOfDate = chartDataSchema.properties.findIndex((property: any) => property.name === 'date');
  const selectedPeriodRecords = getSelectedPeriodRecords({
    chartDataRecords,
    testStartDate,
    indexOfDate,
    isTestPeriodHidden: !isTestPeriodShown,
  });

  return chartDataSchema.properties
    .filter((property: any) => property.name !== 'date')
    .map((property: any, index: number) => ({
      showInLegend: true,
      id: property.name,
      name: property.title ? property.title : name,
      data: selectedPeriodRecords.map((val: any) => [
        moment(val[indexOfDate]).valueOf(),
        property.type === 'permillion' ? val[index + indexOfProperty] * 1000000 : val[index + indexOfProperty],
      ]),
      lineWidth: 1,
      states: {
        hover: {
          enabled: true,
          lineWidth: 1,
        },
      },
    }));
};

export const setProfilePerformanceData = (
  chartDataRecords: any[][],
  indexOfProperty: number,
  indexOfDate: number,
  total: PerformanceActivitiesDetail | null,
  valueType: string,
  isCumulative: boolean = false
) => {
  const dates: (string | number)[][] = [];
  let dateRecordIndex: number = 0;
  const start: string | null = total !== null ? total.start : null;
  const end: string | null = total !== null ? total.end : null;
  let previous: string | number = 0;
  if (start && end) {
    let startDate = moment(start, 'YYYY-MM-DD');
    if (chartDataRecords[dateRecordIndex] && chartDataRecords[dateRecordIndex][indexOfDate]) {
      const startDateRecords = moment(chartDataRecords[dateRecordIndex][indexOfDate], 'YYYY-MM-DD');
      if (startDate.isAfter(startDateRecords)) {
        startDate = startDateRecords;
      }
    }
    const endDate = moment(end, 'YYYY-MM-DD');
    while (!startDate.isAfter(endDate)) {
      const dateExist =
        chartDataRecords[dateRecordIndex] &&
        chartDataRecords[dateRecordIndex][indexOfDate] &&
        startDate.isSame(chartDataRecords[dateRecordIndex][indexOfDate]);

      if (dateExist === true) {
        const value =
          valueType === 'permillion'
            ? convertFromPermillionNumber(chartDataRecords[dateRecordIndex][indexOfProperty])
            : chartDataRecords[dateRecordIndex][indexOfProperty];
        dates.push([startDate.valueOf(), value]);
        previous = value;
        dateRecordIndex = dateRecordIndex + 1;
      } else {
        dates.push([startDate.valueOf(), isCumulative ? previous : 0]);
      }
      startDate.add(1, 'd');
    }
  }

  return dates;
};

interface UpdatedRecordSeriesProfilePerformanceParams {
  chartDataSchema: Record<string, any>;
  chartDataRecords: number[][];
  color: string;
  type: string;
  total: any;
  isReverse: boolean;
}

const updatedRecordSeriesProfilePerformance = ({
  chartDataSchema,
  chartDataRecords,
  color,
  type,
  total,
  isReverse = false,
}: UpdatedRecordSeriesProfilePerformanceParams) => {
  const colors = [color, '#47b74a', '#6E47D1', '#ACD147'];
  const indexOfDate = chartDataSchema.properties.findIndex((property: any) => property.name === 'date');

  const series = chartDataSchema.properties
    .filter((property: any) => property.name !== 'date')
    .map((property: any, index: number) => {
      const indexOfProperty: number = chartDataSchema.properties.findIndex(
        (currentProperty: any) => currentProperty.name === property.name
      );
      const valueType = chartDataSchema.properties[indexOfProperty].type;

      return {
        showInLegend: true,
        color: colors[index],
        name: property.title,
        originalName: property.name,
        data: setProfilePerformanceData(chartDataRecords, indexOfProperty, indexOfDate, total, valueType, type === 'CUMULATIVE'),
        lineWidth: 1,
        dataGrouping: {
          enabled: false,
        },
        states: {
          hover: {
            enabled: true,
            lineWidth: 1,
          },
        },
      };
    });

  return isReverse ? series.reverse() : series;
};

const updatedRecordSeriesPieChart = ({ chartDataSchema, chartDataRecords }: any) => [
  {
    data: chartDataSchema.properties.map((property: any, index: number) => ({
      name: property.title,
      y: chartDataRecords[0][index],
    })),
  },
];
interface UpdatedRecordSeriesBarChartParams {
  chartDataSchema: Record<string, any>;
  chartDataRecords: (string | number)[][];
  recordSet: string;
}

export const updatedRecordSeriesBarChart = ({ chartDataSchema, chartDataRecords, recordSet }: UpdatedRecordSeriesBarChartParams) => {
  const barChartsWithoutLegend = new Set([
    'sharpe-by-capitalization',
    'sharpe-by-industry',
    'sharpe-by-sector',
    'sharpe-by-country',
    'sharpe-by-geography',
  ]);
  const hasNamesInRecords = barChartsWithoutLegend.has(recordSet);
  const indexSeries = chartDataSchema.properties.findIndex((property: any) => property.name === 'series');
  const schemaData =
    recordSet === 'coverage'
      ? chartDataSchema.properties.filter((property: any) => property.name !== 'series')
      : chartDataSchema.properties;

  return hasNamesInRecords
    ? chartDataRecords.map((record: any, index: number) => ({
        name: schemaData[index].title,
        data: record.map((val: number) => val),
        showInLegend: false,
      }))
    : chartDataRecords.map((record: any) => ({
        name: record[indexSeries],
        data: record
          .filter((_: any, index: number) => index !== indexSeries)
          .map((val: number) => (recordSet === 'Sharpe' ? val : val * 100)),
        showInLegend: recordSet === 'Sharpe' ? false : true,
      }));
};

export const chartMapping: any = {
  'average-size-by-capitalization': updatedRecordSeriesPieChart,
  'average-size-by-country': updatedRecordSeriesPieChart,
  'average-size-by-industry': updatedRecordSeriesPieChart,
  'average-size-by-sector': updatedRecordSeriesPieChart,
  'average-size-by-geography': updatedRecordSeriesPieChart,
  'average-value-by-country': updatedRecordSeriesHighStockChart,
  'average-value-by-industry': updatedRecordSeriesHighStockChart,
  'average-value-by-sector': updatedRecordSeriesHighStockChart,
  'average-value-by-geography': updatedRecordSeriesHighStockChart,
  'coverage-by-country': updatedRecordSeriesBarChart,
  'coverage-by-industry': updatedRecordSeriesBarChart,
  'coverage-by-sector': updatedRecordSeriesBarChart,
  'coverage-by-geography': updatedRecordSeriesBarChart,
  'pnl-by-capitalization': updatedRecordSeriesHighStockChart,
  'pnl-by-country': updatedRecordSeriesHighStockChart,
  'pnl-by-industry': updatedRecordSeriesHighStockChart,
  'pnl-by-sector': updatedRecordSeriesHighStockChart,
  'pnl-by-geography': updatedRecordSeriesHighStockChart,
  'sharpe-by-industry': updatedRecordSeriesBarChart,
  'sharpe-by-country': updatedRecordSeriesBarChart,
  'sharpe-by-sector': updatedRecordSeriesBarChart,
  'sharpe-by-capitalization': updatedRecordSeriesBarChart,
  'sharpe-by-geography': updatedRecordSeriesBarChart,
  coverage: updatedRecordSeriesPerformance,
  'dataset-coverage': updatedRecordSeriesPerformance,
  turnover: updatedRecordSeriesPerformance,
  pnl: updatedRecordSeriesPerformance,
  sharpe: updatedRecordSeriesPerformance,
  distribution: updatedRecordSeriesDistribution,
  'performance-profile': updatedRecordSeriesProfilePerformance,
  'actual-weight': updatedRecordSeriesHighStockChart,
};

function getStartDateIndex(chartSeries: any[][], indexOfDate: number, startDate: string) {
  let start = 0;
  let end = chartSeries.length - 1;
  const startMoment = moment.utc(startDate);
  let result = -1;

  while (start <= end) {
    let mid = Math.floor((start + end) / 2);

    const currentMoment = moment.utc(chartSeries[mid][indexOfDate]);
    if (currentMoment.isSame(startMoment)) {
      return mid;
    }

    if (currentMoment.isAfter(startMoment)) {
      end = mid - 1;
    } else {
      start = mid + 1;
      result = mid;
    }
  }

  if (result !== -1) {
    return result + 1 < chartSeries.length ? result + 1 : result;
  } else {
    return -1;
  }
}
